import { useCallback, useEffect, useRef, useState } from "react";
import { CityScene } from "./city/CityScene.tsx";
import { PlanSheetView } from "./city/PlanSheetView.tsx";
import { useCityData } from "./cityscape/useCityData.ts";
import { lookupPortfolioMetrics, usePortfolioStats } from "./cityscape/usePortfolioStats.ts";

const PLAN_EXIT_MS = 420;

export function App() {
	const portfolio = usePortfolioStats();
	const { buildings, loading } = useCityData({
		portfolioMetrics: portfolio.enabled && portfolio.loaded && !portfolio.error ? portfolio.byBundleId : undefined,
	});
	const [selectedId, setSelectedId] = useState<string | null>(null);
	const [planExiting, setPlanExiting] = useState(false);
	const planExitBusy = useRef(false);

	const selectedBuilding = selectedId ? (buildings.find((b) => b.app._id === selectedId) ?? null) : null;

	const storeMetrics = selectedBuilding ? lookupPortfolioMetrics(portfolio.byBundleId, selectedBuilding.app.bundleId) : null;

	const closePlan = useCallback(() => {
		if (planExitBusy.current) return;
		planExitBusy.current = true;
		setPlanExiting(true);
		window.setTimeout(() => {
			setSelectedId(null);
			setPlanExiting(false);
			planExitBusy.current = false;
		}, PLAN_EXIT_MS);
	}, []);

	useEffect(() => {
		setPlanExiting(false);
		planExitBusy.current = false;
	}, [selectedId]);

	useEffect(() => {
		if (!selectedId) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") closePlan();
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [selectedId, closePlan]);

	const detailOpen = Boolean(selectedBuilding);

	return (
		<div className="relative h-screen w-screen overflow-hidden bg-transparent text-[var(--color-text)]">
			{loading && (
				<div className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center bg-[color-mix(in_srgb,var(--color-paper)_90%,white)] backdrop-blur-[2px]">
					<div className="text-sm text-[var(--color-text-muted)]">Loading registry…</div>
				</div>
			)}

			{!loading && buildings.length === 0 && (
				<div className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center">
					<div className="text-sm text-[var(--color-text-muted)]">No projects in the register yet.</div>
				</div>
			)}

			<div
				className={`city-stage absolute inset-0 transition-all duration-500 ease-[cubic-bezier(0.33,1,0.68,1)] ${
					detailOpen ? "city-stage-dim scale-[0.94] opacity-[0.38]" : "scale-100 opacity-100"
				}`}
				style={{ transformOrigin: "50% 45%" }}
			>
				<CityScene buildings={buildings} selectedId={selectedId} onSelect={setSelectedId} detailOpen={detailOpen} />
			</div>

			{selectedBuilding ? (
				<PlanSheetView
					building={selectedBuilding}
					exiting={planExiting}
					onClose={closePlan}
					storeMetrics={storeMetrics}
					portfolioConfigured={portfolio.enabled}
					portfolioLoaded={portfolio.loaded}
					portfolioFetchFailed={portfolio.enabled && portfolio.loaded && portfolio.error}
				/>
			) : null}
		</div>
	);
}
